import React, { useState, useEffect } from 'react';
import {
  Plus, Edit2, Trash2, Search, X, Users as UsersIcon,
  Key, AlertTriangle, CheckCircle, Calendar
} from 'lucide-react';
import apiClient from '../services/api';
import '../styles/MasterForms.css';

const Users = () => {
  const [users,         setUsers]         = useState([]);
  const [employees,     setEmployees]     = useState([]);
  const [userTypes,     setUserTypes]     = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [searchTerm,    setSearchTerm]    = useState('');
  const [showModal,     setShowModal]     = useState(false);
  const [editMode,      setEditMode]      = useState(false);

  const [subInfo, setSubInfo] = useState({
    has_subscription: false, max_users: 0, active_count: 0, can_add: false,
    duration_months: 0, subscriptionid: null,
  });

  const [formData, setFormData] = useState({
    userid: null, username: '', password: '', fullname: '', email: '',
    employeeid: '', usertypeid: '', usertype: 'User',
    isactive: true,
    subscription_expiry: '',
  });
  const [errors,        setErrors]        = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showPassword,  setShowPassword]  = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchEmployees();
    fetchUserTypes();
  }, []);

  useEffect(() => { filterUsers(); }, [searchTerm, users]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/Users.php');
      if (res.data.success) {
        setUsers(res.data.data || []);
        if (res.data.subscription_info) setSubInfo(res.data.subscription_info);
      }
    } catch (e) {
      console.error('Error fetching users:', e);
      if (e.response?.status === 401) alert('Session expired. Please login again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await apiClient.get('/Employees.php', { params: { active: true } });
      if (res.data.success) setEmployees(res.data.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchUserTypes = async () => {
    try {
      const res = await apiClient.get('/UserTypes.php', { params: { active: true } });
      if (res.data.success) setUserTypes(res.data.data || []);
    } catch (e) { console.error(e); }
  };

  const filterUsers = () => {
    if (!searchTerm.trim()) { setFilteredUsers(users); return; }
    const q = searchTerm.toLowerCase();
    setFilteredUsers(users.filter(u =>
      u.username?.toLowerCase().includes(q) ||
      u.fullname?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.employeename?.toLowerCase().includes(q)
    ));
  };

  const validateForm = () => {
    const e = {};
    if (!formData.username.trim())                              e.username = 'Username is required';
    else if (formData.username.length > 50)                    e.username = 'Max 50 characters';
    if (!editMode && !formData.password.trim())                e.password = 'Password is required';
    else if (formData.password && formData.password.length < 6) e.password = 'Min 6 characters';
    if (!formData.fullname.trim())                             e.fullname = 'Full name is required';
    else if (formData.fullname.length > 100)                   e.fullname = 'Max 100 characters';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = 'Invalid email';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validateForm()) return;
    setSubmitLoading(true);
    try {
      const method  = editMode ? 'put' : 'post';
      const payload = {
        ...formData,
        employeeid: formData.employeeid !== '' ? parseInt(formData.employeeid) : null,
        usertypeid: formData.usertypeid !== '' ? parseInt(formData.usertypeid) : null,
        isactive:   formData.isactive ? 1 : 0,
      };
      if (editMode && !formData.password) delete payload.password;

      const res = await apiClient[method]('/Users.php', payload);
      if (res.data.success) {
        if (res.data.subscription_info) setSubInfo(res.data.subscription_info);
        alert(res.data.message || (editMode ? 'User updated!' : 'User created!'));
        setShowModal(false);
        resetForm();
        fetchUsers();
      } else {
        alert('Error: ' + res.data.message);
      }
    } catch (error) {
      if (error.response?.status === 401)  alert('Session expired. Please login again.');
      else if (error.response?.status === 403) alert(error.response.data.message);
      else if (error.response?.status === 409) alert('Username already exists.');
      else alert('Error saving user. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (user) => {
    setFormData({
      userid: user.userid, username: user.username, password: '',
      fullname: user.fullname, email: user.email || '',
      employeeid: user.employeeid || '', usertypeid: user.usertypeid || '',
      usertype: user.usertype || 'User',
      isactive: user.isactive === 1,
      subscription_expiry: user.subscription_expiry || '',
    });
    setEditMode(true); setShowModal(true); setErrors({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      const res = await apiClient.delete('/Users.php', { data: { userid: id } });
      if (res.data.success) {
        if (res.data.subscription_info) setSubInfo(res.data.subscription_info);
        fetchUsers();
      } else { alert('Error: ' + res.data.message); }
    } catch (error) {
      if (error.response?.status === 400) alert('Cannot delete your own account.');
      else alert('Error deleting user.');
    }
  };

  const resetForm = () => {
    setFormData({
      userid: null, username: '', password: '', fullname: '', email: '',
      employeeid: '', usertypeid: '', usertype: 'User',
      isactive: true, subscription_expiry: '',
    });
    setEditMode(false); setErrors({}); setShowPassword(false);
  };

  const openAddModal = () => {
    if (!subInfo.has_subscription) {
      alert('No active subscription found.\n\nPlease go to Subscription Management and purchase a subscription before adding users.');
      return;
    }
    if (!subInfo.can_add) {
      alert(`User limit reached!\n\nYour subscription allows ${subInfo.max_users} active users.\nCurrently active: ${subInfo.active_count}\n\nDeactivate an existing user or purchase additional user slots.`);
      return;
    }
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); resetForm(); };

  const getLimitColor = () => {
    if (!subInfo.has_subscription) return '#dc2626';
    const pct = subInfo.active_count / subInfo.max_users;
    if (pct >= 1)   return '#dc2626';
    if (pct >= 0.8) return '#f59e0b';
    return '#16a34a';
  };

  const fmtDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' });
  };

  const today = new Date().toISOString().split('T')[0];

  // Preview expiry for new user: today + duration_months
  const expiryPreview = (() => {
    if (!subInfo.has_subscription || !subInfo.duration_months) return null;
    const d = new Date();
    d.setMonth(d.getMonth() + subInfo.duration_months);
    return d.toISOString().split('T')[0];
  })();

  return (
    <div className="master-container">
      {/* ── Header ── */}
      <div className="master-header">
        <div className="header-left">
          <h2>
            <UsersIcon size={32} style={{ display:'inline', marginRight:10, verticalAlign:'middle' }} />
            Users Master
          </h2>
          <p className="subtitle">Manage user accounts and access control</p>
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          {/* Subscription status badge */}
          <div style={{
            display:'flex', alignItems:'center', gap:6,
            padding:'6px 14px', borderRadius:20,
            background: getLimitColor() + '18',
            border: `1.5px solid ${getLimitColor()}`,
            color: getLimitColor(), fontWeight:700, fontSize:'0.82rem',
          }}>
            {subInfo.has_subscription
              ? <><CheckCircle size={14}/> {subInfo.active_count}/{subInfo.max_users} users · {subInfo.duration_months}mo plan</>
              : <><AlertTriangle size={14}/> No subscription</>
            }
          </div>

          <button
            className="btn-primary"
            onClick={openAddModal}
            disabled={!subInfo.has_subscription || !subInfo.can_add}
            style={{
              opacity: (!subInfo.has_subscription || !subInfo.can_add) ? 0.5 : 1,
              cursor:  (!subInfo.has_subscription || !subInfo.can_add) ? 'not-allowed' : 'pointer',
            }}
            title={!subInfo.has_subscription ? 'No active subscription' : !subInfo.can_add ? `Limit reached (${subInfo.active_count}/${subInfo.max_users})` : 'Add new user'}
          >
            <Plus size={20} /> Add User
          </button>
        </div>
      </div>

      {/* No-subscription warning banner */}
      {!subInfo.has_subscription && (
        <div style={{
          background:'#fef2f2', border:'1px solid #fecaca', borderRadius:10,
          padding:'14px 18px', marginBottom:16, display:'flex',
          alignItems:'center', gap:10, color:'#dc2626',
        }}>
          <AlertTriangle size={20}/>
          <span>
            <strong>No active subscription.</strong> You cannot add new users until a subscription is purchased.
            Please go to <strong>Subscription Management</strong> to purchase user slots.
          </span>
        </div>
      )}

      {/* ── Search bar ── */}
      <div className="master-toolbar">
        <div className="search-box">
          <Search size={20}/>
          <input type="text" placeholder="Search by username, name, email, or employee..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}><X size={16}/></button>
          )}
        </div>
        <div className="toolbar-info">Total: {filteredUsers.length} {filteredUsers.length === 1 ? 'user' : 'users'}</div>
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div className="loading-state">Loading users...</div>
      ) : filteredUsers.length === 0 ? (
        <div className="empty-state">
          <p>No users found</p>
          {searchTerm && <button onClick={() => setSearchTerm('')}>Clear search</button>}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Full Name</th>
                <th>Email</th>
                <th>Employee</th>
                <th>User Type</th>
                <th>Status</th>
                <th>Subscription Expiry</th>
                <th>Last Login</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr key={user.userid} className={user.sub_expired ? 'row-expired' : ''}>
                  <td className="font-semibold">{user.username}</td>
                  <td>{user.fullname}</td>
                  <td>{user.email || '-'}</td>
                  <td>
                    {user.employeename
                      ? <span className="country-badge">{user.employeecode} - {user.employeename}</span>
                      : '-'}
                  </td>
                  <td><span className="country-badge">{user.usertypename || user.usertype || 'User'}</span></td>
                  <td>
                    <span className={`status-badge ${user.isactive ? 'active' : 'inactive'}`}>
                      {user.isactive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    {user.subscription_expiry ? (
                      <div>
                        <span className={user.sub_expired ? 'text-red font-semibold' : user.sub_days_left <= 7 ? 'text-amber' : ''}>
                          {fmtDate(user.subscription_expiry)}
                        </span>
                        {user.sub_expired
                          ? <div style={{ fontSize:'0.72rem', color:'#dc2626' }}>⚠️ Expired</div>
                          : user.sub_days_left <= 7
                            ? <div style={{ fontSize:'0.72rem', color:'#f59e0b' }}>{user.sub_days_left}d left</div>
                            : null}
                      </div>
                    ) : (
                      <span style={{ color:'#9ca3af', fontSize:'0.8rem' }}>—</span>
                    )}
                  </td>
                  <td>{user.lastlogin ? new Date(user.lastlogin).toLocaleDateString() : 'Never'}</td>
                  <td className="actions-column">
                    <button className="btn-icon btn-edit" onClick={() => handleEdit(user)} title="Edit"><Edit2 size={16}/></button>
                    <button className="btn-icon btn-delete" onClick={() => handleDelete(user.userid)} title="Delete"><Trash2 size={16}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Modal ── */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit User' : 'Add New User'}</h3>
              <button className="btn-close" onClick={closeModal}><X size={24}/></button>
            </div>

            {/* Add mode: show auto-expiry preview */}
            {!editMode && subInfo.has_subscription && expiryPreview && (
              <div style={{
                margin:'0 0 14px', padding:'10px 14px', borderRadius:8,
                background:'#f0fdf4', border:'1px solid #bbf7d0', color:'#15803d',
                fontSize:'0.82rem', display:'flex', alignItems:'center', gap:8,
              }}>
                <CheckCircle size={14}/>
                Slot {subInfo.active_count + 1} of {subInfo.max_users} · This user will expire on{' '}
                <strong style={{ marginLeft:4 }}>{fmtDate(expiryPreview)}</strong>
                &nbsp;({subInfo.duration_months} month{subInfo.duration_months > 1 ? 's' : ''} from today)
              </div>
            )}

            {/* Edit mode: show current expiry status */}
            {editMode && (
              <div style={{
                margin:'0 0 14px', padding:'10px 16px', borderRadius:8,
                background:'#f8fafc', border:'1px solid #e2e8f0',
                fontSize:'0.82rem',
              }}>
                <div style={{ fontWeight:700, color:'#374151', marginBottom:8, display:'flex', alignItems:'center', gap:6 }}>
                  <Calendar size={14}/> Subscription Expiry
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                  <span style={{ color: formData.subscription_expiry < today ? '#dc2626' : '#15803d', fontWeight:600 }}>
                    {formData.subscription_expiry
                      ? formData.subscription_expiry < today
                        ? `⚠ Expired on ${fmtDate(formData.subscription_expiry)}`
                        : `Active until ${fmtDate(formData.subscription_expiry)}`
                      : 'No expiry set'}
                  </span>
                  <input
                    type="date"
                    value={formData.subscription_expiry}
                    onChange={e => setFormData({ ...formData, subscription_expiry: e.target.value })}
                    style={{ fontSize:12, padding:'3px 8px', border:'1px solid #e2e8f0', borderRadius:6, marginLeft:'auto' }}
                    title="Admin override: change expiry date"
                  />
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="modal-form">
              {/* Username & Password */}
              <div className="form-row">
                <div className="form-group">
                  <label>Username <span className="required">*</span></label>
                  <input type="text" value={formData.username}
                    onChange={e => setFormData({...formData, username: e.target.value})}
                    maxLength="50" placeholder="e.g., john.doe"
                    className={errors.username ? 'error' : ''} readOnly={editMode}/>
                  {errors.username && <span className="error-message">{errors.username}</span>}
                </div>
                <div className="form-group">
                  <label>Password {!editMode && <span className="required">*</span>}</label>
                  <div style={{ position:'relative' }}>
                    <input type={showPassword ? 'text' : 'password'} value={formData.password}
                      onChange={e => setFormData({...formData, password: e.target.value})}
                      placeholder={editMode ? 'Leave blank to keep current' : 'Enter password'}
                      className={errors.password ? 'error' : ''}/>
                    <button type="button" onClick={() => setShowPassword(!showPassword)} style={{
                      position:'absolute', right:10, top:'50%', transform:'translateY(-50%)',
                      background:'none', border:'none', cursor:'pointer', color:'#6b7280'
                    }}><Key size={16}/></button>
                  </div>
                  {errors.password && <span className="error-message">{errors.password}</span>}
                </div>
              </div>

              {/* Full name & Email */}
              <div className="form-row">
                <div className="form-group">
                  <label>Full Name <span className="required">*</span></label>
                  <input type="text" value={formData.fullname}
                    onChange={e => setFormData({...formData, fullname: e.target.value})}
                    maxLength="100" placeholder="e.g., John Doe"
                    className={errors.fullname ? 'error' : ''}/>
                  {errors.fullname && <span className="error-message">{errors.fullname}</span>}
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={formData.email}
                    onChange={e => setFormData({...formData, email: e.target.value})}
                    maxLength="100" placeholder="e.g., john@example.com"
                    className={errors.email ? 'error' : ''}/>
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>
              </div>

              {/* Employee & User Type */}
              <div className="form-row">
                <div className="form-group">
                  <label>Employee</label>
                  <select value={formData.employeeid}
                    onChange={e => setFormData({...formData, employeeid: e.target.value})}>
                    <option value="">-- No Employee Link --</option>
                    {employees.map(emp => (
                      <option key={emp.employeeid} value={emp.employeeid}>
                        {emp.employeecode} - {emp.employeename}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>User Type</label>
                  <select value={formData.usertypeid}
                    onChange={e => setFormData({...formData, usertypeid: e.target.value})}>
                    <option value="">-- Select User Type --</option>
                    {userTypes.map(t => (
                      <option key={t.usertypeid} value={t.usertypeid}>{t.usertypename}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Active */}
              <div className="form-row">
                <div className="form-group">
                  <label className="checkbox-label">
                    <input type="checkbox" checked={formData.isactive}
                      onChange={e => setFormData({...formData, isactive: e.target.checked})}/>
                    <span>Active</span>
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeModal} disabled={submitLoading}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={submitLoading}>
                  {submitLoading ? 'Saving...' : editMode ? 'Update' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;